import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "@/context/AuthContext";
import { useT } from "@/i18n/LocaleContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

export default function MachineSpecs() {
  const { t, dict } = useT();
  const highlights = dict.machines.highlights;
  const cols = dict.machines.cols;
  const [machines, setMachines] = useState(null);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/machines`)
      .then((r) => { if (active) setMachines(r.data || []); })
      .catch(() => { if (active) setMachines([]); });
    return () => { active = false; };
  }, []);

  return (
    <section id="maschinenpark" className="py-32 lg:py-44 bg-white" data-testid="machines-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-16 lg:mb-20"
        >
          <p className="font-tech text-[11px] tracking-[0.3em] uppercase text-[var(--ci-blue)] mb-6">{t("machines.eyebrow")}</p>
          <h2 className="text-3xl lg:text-5xl font-bold tracking-tight max-w-3xl text-[var(--ci-grey)]">
            {t("machines.title1")}<span className="text-[var(--ci-blue)]">{t("machines.title2")}</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-[#E3E8EC] rounded-2xl overflow-hidden mb-20"
          data-testid="machine-highlights"
        >
          {highlights.map(([value, label]) => (
            <div key={label} className="border-b border-r border-[#E3E8EC] px-5 sm:px-8 py-10 lg:py-14 bg-white overflow-hidden">
              <div className="font-tech text-2xl lg:text-4xl text-[var(--ci-grey)] whitespace-nowrap">{value}</div>
              <div className="font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)] mt-3">{label}</div>
            </div>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 gap-8 lg:gap-10" data-testid="machine-cards">
          {machines === null ? (
            <div className="text-center text-sm text-[var(--ci-grey)] py-16" data-testid="machines-loading">Lade Maschinenpark …</div>
          ) : machines.length === 0 ? (
            <div className="text-center text-sm text-[var(--ci-grey)] py-16 border border-dashed border-[#C3CCD4] rounded-2xl" data-testid="machines-empty">
              Aktuell sind keine Maschinen hinterlegt.
            </div>
          ) : machines.map((m, i) => (
            <motion.article
              key={m.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.05 }}
              data-testid={`machine-card-${i}`}
              className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-[#E3E8EC] rounded-2xl overflow-hidden bg-white shadow-[0_4px_40px_rgba(17,24,39,0.04)] hover:shadow-[0_12px_60px_rgba(17,24,39,0.08)] transition-shadow duration-500"
            >
              <div className="lg:col-span-5 relative aspect-[16/10] lg:aspect-auto lg:min-h-[280px] overflow-hidden bg-[#EEF1F4] group">
                {m.image_url ? (
                  <img
                    src={resolveImg(m.image_url)}
                    alt={m.name}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-[var(--ci-grey)]/40 font-tech text-xs tracking-[0.2em] uppercase">Kein Bild</div>
                )}
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full font-tech text-[9px] tracking-[0.25em] uppercase text-[var(--ci-blue)]">
                  {String(i + 1).padStart(2, "0")} / {String(machines.length).padStart(2, "0")}
                </div>
              </div>
              <div className="lg:col-span-7 p-7 sm:p-10 lg:p-12 flex flex-col justify-center">
                <h3 className="text-xl sm:text-2xl font-bold text-[var(--ci-grey)] leading-tight">{m.name}</h3>
                <p className="mt-2 font-tech text-[11px] tracking-[0.22em] uppercase text-[var(--ci-turquoise)]">{m.technology}</p>
                <dl className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 border-t border-[#EEF1F4] pt-6">
                  <div>
                    <dt className="font-tech text-[10px] tracking-[0.25em] uppercase text-[var(--ci-grey)] opacity-70">{cols[2]}</dt>
                    <dd className="mt-2 font-tech text-base text-[var(--ci-blue)] font-semibold">{m.capacity || "—"}</dd>
                  </div>
                  <div>
                    <dt className="font-tech text-[10px] tracking-[0.25em] uppercase text-[var(--ci-grey)] opacity-70">{cols[3]}</dt>
                    <dd className="mt-2 text-sm text-[var(--ci-grey)] leading-relaxed">{m.notes || "—"}</dd>
                  </div>
                </dl>
              </div>
            </motion.article>
          ))}
        </div>

        <p className="mt-14 text-sm text-[var(--ci-grey)] max-w-2xl leading-relaxed">
          {t("machines.note")}
        </p>
      </div>
    </section>
  );
}
